import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';

import useLocalization from '../../hooks/useLocalization';
import { useSelectWelcomeBonus } from '../../hooks/useWelcomeBonus';
import { WelcomeBonusAvailabilityResponse, WelcomeBonusOption } from '../../types/bonus';

import { mergeSx } from '../../utils/styles';

const WELCOME_BONUS_OPTION_IMAGES: Record<Exclude<WelcomeBonusOption, WelcomeBonusOption.NO_BONUS>, string> = {
  [WelcomeBonusOption.SPORTSBOOK]: '/icons/neobet-sports-banner.webp',
  [WelcomeBonusOption.CASINO]: '/icons/neobet-casino-banner.webp',
};

const styles = {
  root: {
    p: 2,
    borderRadius: 2,
    backgroundColor: 'neutral.600',
    borderColor: 'neutral.500',
    color: 'neutral.25',
  },
  header: {
    gap: 0.5,
    pb: 1.25,
    borderBottom: '1px solid',
    borderColor: 'neutral.500',
  },
  title: {
    fontWeight: 800,
    lineHeight: 1.2,
  },
  subtitle: {
    color: 'neutral.300',
    lineHeight: 1.35,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 1.25,
  },
  bonusCard: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'neutral.500',
    color: 'neutral.25',
    textAlign: 'left',
    backgroundColor: 'neutral.700',
    '&:hover': {
      borderColor: 'primary.main',
    },
    '&.Mui-disabled': {
      color: 'neutral.300',
      borderColor: 'neutral.600',
      opacity: 1,
      pointerEvents: 'none',
      filter: 'grayscale(0.75)',
    },
  },
  bonusCardImage: {
    width: '100%',
    aspectRatio: '16 / 9',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.02) 35%, rgba(0, 0, 0, 0.58) 100%)',
    },
  },
  bonusCardLabel: {
    position: 'absolute',
    left: 8,
    right: 8,
    bottom: 8,
    px: 1,
    py: 0.75,
    borderRadius: 1,
    color: 'neutral.100',
    background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.62) 0%, rgba(0, 0, 0, 0.28) 78%, rgba(0, 0, 0, 0) 100%)',
    zIndex: 1,
  },
  bonusCardTitle: {
    color: 'inherit',
    fontWeight: 800,
    typography: 'body3',
    lineHeight: 1.2,
  },
  noBonusButton: {
    width: '100%',
    minHeight: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    px: 1.75,
    py: 1,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'neutral.500',
    color: 'neutral.50',
    backgroundColor: 'transparent',
    textAlign: 'left',
    '&:hover': {
      borderColor: 'neutral.300',
    },
    '&.Mui-disabled': {
      color: 'neutral.300',
      borderColor: 'neutral.600',
      opacity: 1,
      pointerEvents: 'none',
    },
  },
  noBonusContent: {
    width: '100%',
    alignItems: 'flex-start',
    gap: 0.35,
  },
  optionTitle: {
    color: 'inherit',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  optionDescription: {
    color: 'inherit',
    opacity: 0.72,
    lineHeight: 1.3,
  },
  confirmDialogPaper: {
    p: 3,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    backgroundImage: 'none',
    color: 'text.primary',
  },
  confirmDialogTitle: {
    p: 0,
    mb: 1.5,
    typography: 'h3',
    textAlign: 'center',
  },
  confirmDialogContent: {
    p: 0,
    textAlign: 'center',
  },
  confirmDialogMessage: {
    color: 'text.secondary',
  },
  confirmDialogActions: {
    p: 0,
    pt: 3,
  },
  confirmActionRow: {
    width: '100%',
  },
  confirmCancelButton: {
    flex: 1,
    borderRadius: 2,
    color: 'neutral.300',
    borderColor: 'neutral.400',
    '&:hover': {
      borderColor: 'neutral.300',
      backgroundColor: 'transparent',
    },
  },
  confirmSubmitButton: {
    flex: 1,
    borderRadius: 2,
  },
} as const;

type Props = {
  availability: WelcomeBonusAvailabilityResponse;
};

type PendingSelection = {
  option: WelcomeBonusOption;
  label: string;
};

type WelcomeBonusOptionCardProps = {
  disabled: boolean;
  imageUrl: string;
  label: string;
  onSelect: () => void;
};

const WelcomeBonusOptionCard = ({ disabled, imageUrl, label, onSelect }: WelcomeBonusOptionCardProps) => (
  <ButtonBase sx={styles.bonusCard} disabled={disabled} onClick={onSelect}>
    <Box sx={{ ...styles.bonusCardImage, backgroundImage: `url(${imageUrl})` }} />
    <Box sx={styles.bonusCardLabel}>
      <Typography sx={styles.bonusCardTitle}>{label}</Typography>
    </Box>
  </ButtonBase>
);

type NoWelcomeBonusOptionProps = {
  disabled: boolean;
  label: string;
  description: string;
  onSelect: () => void;
};

const NoWelcomeBonusOption = ({ disabled, label, description, onSelect }: NoWelcomeBonusOptionProps) => (
  <ButtonBase sx={styles.noBonusButton} disabled={disabled} onClick={onSelect}>
    <Stack
      direction='row'
      sx={mergeSx(
        {
          alignItems: 'flex-start',
        },
        styles.noBonusContent
      )}
    >
      <Stack sx={styles.noBonusContent}>
        <Typography sx={styles.optionTitle}>{label}</Typography>
        <Typography variant='body3' sx={styles.optionDescription}>
          {description}
        </Typography>
      </Stack>
    </Stack>
  </ButtonBase>
);

type WelcomeBonusConfirmDialogProps = {
  isLoading: boolean;
  isNoBonusSelection: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  optionLabel?: string;
  open: boolean;
};

const WelcomeBonusConfirmDialog = ({
  isLoading,
  isNoBonusSelection,
  onCancel,
  onConfirm,
  open,
  optionLabel,
}: WelcomeBonusConfirmDialogProps) => {
  const { t } = useLocalization();

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isLoading) {
          onCancel();
        }
      }}
      maxWidth='xs'
      fullWidth
      slotProps={{
        paper: { sx: styles.confirmDialogPaper },
      }}
    >
      <DialogTitle sx={styles.confirmDialogTitle}>{t('welcomeBonus.confirmTitle')}</DialogTitle>
      <DialogContent sx={styles.confirmDialogContent}>
        <Typography variant='body2' sx={styles.confirmDialogMessage}>
          {isNoBonusSelection
            ? t('welcomeBonus.confirmNoBonusMessage')
            : t('welcomeBonus.confirmBonusMessage', { option: optionLabel })}
        </Typography>
      </DialogContent>
      <DialogActions sx={styles.confirmDialogActions}>
        <Stack direction='row' spacing={2} sx={styles.confirmActionRow}>
          <Button variant='outlined' onClick={onCancel} disabled={isLoading} sx={styles.confirmCancelButton}>
            {t('cancel')}
          </Button>
          <Button variant='contained' onClick={onConfirm} disabled={isLoading} sx={styles.confirmSubmitButton}>
            {isLoading ? <CircularProgress size={18} color='inherit' /> : t('confirm')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const WelcomeBonusSelection = ({ availability }: Props) => {
  const { t } = useLocalization();
  const { mutate, isPending: isLoading } = useSelectWelcomeBonus();
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);

  const handleSelect = (selection: PendingSelection) => {
    setPendingSelection(selection);
  };

  const handleConfirmSelection = () => {
    if (!pendingSelection) return;

    mutate(
      { option: pendingSelection.option },
      {
        onError: () => {
          enqueueSnackbar(t('welcomeBonus.selectionFailed'), { variant: 'error' });
        },
        onSuccess: () => setPendingSelection(null),
      }
    );
  };

  const sportsbook = availability.options.sportsbook;
  const casino = availability.options.casino;
  const sportsbookLabel = t('welcomeBonus.sportsbookTitle');
  const casinoLabel = t('welcomeBonus.casinoTitle');
  const noBonusLabel = t('welcomeBonus.noBonusTitle');
  const isNoBonusSelection = pendingSelection?.option === WelcomeBonusOption.NO_BONUS;
  const openSelectionConfirmation = (option: WelcomeBonusOption, label: string) => {
    handleSelect({ option, label });
  };

  return (
    <>
      <Paper variant='outlined' sx={styles.root}>
        <Stack spacing={1.25}>
          <Stack sx={styles.header}>
            <Typography variant='h4' sx={styles.title}>
              {t('welcomeBonus.selectionTitle')}
            </Typography>
            <Typography variant='body3' sx={styles.subtitle}>
              {t('welcomeBonus.selectionDescription')}
            </Typography>
          </Stack>

          <Box sx={styles.cardsGrid}>
            <WelcomeBonusOptionCard
              disabled={!sportsbook || isLoading}
              imageUrl={WELCOME_BONUS_OPTION_IMAGES[WelcomeBonusOption.SPORTSBOOK]}
              onSelect={() => openSelectionConfirmation(WelcomeBonusOption.SPORTSBOOK, sportsbookLabel)}
              label={sportsbookLabel}
            />

            <WelcomeBonusOptionCard
              disabled={!casino || isLoading}
              imageUrl={WELCOME_BONUS_OPTION_IMAGES[WelcomeBonusOption.CASINO]}
              onSelect={() => openSelectionConfirmation(WelcomeBonusOption.CASINO, casinoLabel)}
              label={casinoLabel}
            />
          </Box>

          <NoWelcomeBonusOption
            disabled={isLoading}
            onSelect={() => openSelectionConfirmation(WelcomeBonusOption.NO_BONUS, noBonusLabel)}
            label={noBonusLabel}
            description={t('welcomeBonus.noBonusDescription')}
          />
        </Stack>
      </Paper>

      <WelcomeBonusConfirmDialog
        open={!!pendingSelection}
        isLoading={isLoading}
        isNoBonusSelection={isNoBonusSelection}
        onCancel={() => setPendingSelection(null)}
        onConfirm={handleConfirmSelection}
        optionLabel={pendingSelection?.label}
      />
    </>
  );
};

export default WelcomeBonusSelection;
